/**
 * Detection and one-shot recovery for failed dynamic imports.
 *
 * Route pages are React.lazy chunks with content-hashed URLs. When a deploy
 * swaps the server image, the shell an open tab is still running references
 * chunk hashes that no longer exist: the import rejects, and neither the
 * browser's module map nor React's lazy() retries it usefully — only a full
 * document reload (fresh shell, fresh hashes) recovers.
 */

const CHUNK_LOAD_ERROR_PATTERNS = [
  // Chrome/Edge and Safari 16.4+
  /failed to fetch dynamically imported module/i,
  // Firefox
  /importing a module script failed/i,
  // Safari < 16.4
  /error loading dynamically imported module/i,
]

/** True when the error is a browser's wording of a failed dynamic import. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const message = error.message ?? ''
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

const STALE_SHELL_RELOAD_KEY = 'rochd.staleShellReloadAttempted'

/**
 * Reload the document at most once per tab session to recover a stale shell.
 * The sessionStorage guard breaks the reload loop when a fresh shell still
 * fails (deploy in progress, offline): callers then fall back to a manual
 * reload prompt. Returns true when the reload was initiated.
 */
export function reloadForStaleShellOnce(options: {
  storage?: Pick<Storage, 'getItem' | 'setItem'>
  reload?: () => void
} = {}): boolean {
  const storage = options.storage ?? window.sessionStorage
  const reload = options.reload ?? (() => window.location.reload())
  try {
    if (storage.getItem(STALE_SHELL_RELOAD_KEY) === '1') return false
    storage.setItem(STALE_SHELL_RELOAD_KEY, '1')
  } catch {
    // Storage unavailable (rare): decline rather than risk an unrecoverable
    // reload loop — the manual prompt still works.
    return false
  }
  reload()
  return true
}
